/**
 * Dice Registration Types - Shared Contract
 * All agents MUST import from this file
 *
 * @module dice-registry
 */

/** Supported dice types for registration (d100 excluded - too many faces) */
export type DiceType = 'd4' | 'd6' | 'd8' | 'd10' | 'd12' | 'd20';

export const FACE_COUNTS: Record<DiceType, number> = {
  d4: 4,
  d6: 6,
  d8: 8,
  d10: 10,
  d12: 12,
  d20: 20,
};

/**
 * Training modes that enforce image count minimums.
 * Used to key IMAGE_REQUIREMENTS and DEFAULT_DEGREES_BY_MODE.
 */
export type BoundedTrainingIntensity = 'recommended' | 'light';

/**
 * Training intensity determines the number of images required per dice type.
 * - 'recommended': Full coverage with 30-50 images per face for best accuracy
 * - 'light': Faster setup with 30-40 total images per dice type
 * - 'unlimited': No minimum enforced — capture freely, any angle, any background
 */
export type TrainingIntensity = BoundedTrainingIntensity | 'unlimited';

/**
 * Minimum and maximum image counts required for training each dice type.
 * Only applies to bounded intensity modes (recommended and light).
 * For 'unlimited' mode, there is no minimum — check trainingIntensity before indexing.
 *
 * Recommended mode: 30-50 images per face for optimal model accuracy
 * Light mode: 30-40 total images per dice type for faster setup
 */
export const IMAGE_REQUIREMENTS: Record<BoundedTrainingIntensity, Record<DiceType, { min: number; max: number }>> = {
  /**
   * Recommended mode: Full coverage with 30-50 images per face.
   * Provides best recognition accuracy but requires more capture time.
   */
  recommended: {
    d4: { min: 120, max: 200 },   // 30-50 per face x 4 faces
    d6: { min: 180, max: 300 },   // 30-50 per face x 6 faces
    d8: { min: 240, max: 400 },   // 30-50 per face x 8 faces
    d10: { min: 300, max: 500 },  // 30-50 per face x 10 faces
    d12: { min: 360, max: 600 },  // 30-50 per face x 12 faces
    d20: { min: 600, max: 1000 }, // 30-50 per face x 20 faces
  },
  /**
   * Light mode: Minimal coverage with 30-40 total images per dice type.
   * Faster setup with acceptable accuracy for casual use.
   */
  light: {
    d4: { min: 30, max: 40 },
    d6: { min: 30, max: 40 },
    d8: { min: 30, max: 40 },
    d10: { min: 30, max: 40 },
    d12: { min: 30, max: 40 },
    d20: { min: 30, max: 40 },
  },
};

/**
 * Preset degree values for rotation slider.
 * Users select how many degrees to rotate between captures.
 * Lower values = more images per face for better accuracy.
 */
export const DEGREES_PER_CAPTURE_OPTIONS = [15, 30, 45, 60, 90, 180, 360] as const;

/**
 * Type for valid degree per capture values
 */
export type DegreesPerCapture = (typeof DEGREES_PER_CAPTURE_OPTIONS)[number];

/**
 * Calculate the number of captures for a given degree value.
 * @param degrees - Degrees per capture (rotation between captures)
 * @returns Number of captures required for full 360-degree coverage
 */
export function calculateCapturesFromDegrees(degrees: number): number {
  return Math.ceil(360 / degrees);
}

/**
 * Legacy: Number of rotation angles to capture per face for each dice type.
 * @deprecated Use DEFAULT_DEGREES_BY_MODE instead for new code.
 * Kept for backward compatibility with existing registrations.
 */
export const ROTATION_REQUIREMENTS: Record<DiceType, number> = {
  d4: 8,   // tetrahedron limited rotation
  d6: 6,
  d8: 5,
  d10: 4,
  d12: 4,
  d20: 2,  // upright + upside down text
};

/**
 * Default degrees per capture based on training intensity mode.
 * Recommended mode: 45 degrees = 8 captures per face for best accuracy.
 * Light mode: 90 degrees = 4 captures per face for faster setup.
 */
export const DEFAULT_DEGREES_BY_MODE: Record<BoundedTrainingIntensity, DegreesPerCapture> = {
  recommended: 45, // 8 captures per face
  light: 90,       // 4 captures per face
};

/**
 * @deprecated Use DEFAULT_DEGREES_BY_MODE instead.
 * Legacy rotation requirements based on training intensity mode.
 * Kept for backward compatibility.
 */
export const ROTATION_REQUIREMENTS_BY_MODE: Record<BoundedTrainingIntensity, Record<DiceType, number>> = {
  recommended: ROTATION_REQUIREMENTS, // Full coverage
  light: {
    d4: 4,  // Half of 8
    d6: 3,  // Half of 6
    d8: 3,  // ~Half of 5
    d10: 2, // Half of 4
    d12: 2, // Half of 4
    d20: 1, // Half of 2
  }
};

// ========== Dice Face Geometry for Ring Guide ==========

/**
 * Dice face geometry configuration for the registration ring guide.
 * Defines which faces are visible from the side when a specific face is on top.
 */
export interface DiceFaceGeometry {
  /** Number of visible side faces (ring positions) */
  visibleSideCount: number;
  /** Angle between ring positions in degrees */
  ringAngle: number;
  /** Maps top face -> array of visible side faces (clockwise from 12 o'clock) */
  sideMap: Record<number, number[]>;
  /** Maps face -> its opposite face (on bottom when top is up) */
  oppositeMap: Record<number, number>;
  /** Sum of opposite faces for validation, null if no true opposites (D4) */
  oppositeSum: number | null;
}

/**
 * Dice geometry constants for all supported dice types.
 * Based on standard dice conventions where opposite faces sum to (max+1).
 *
 * Ring guide shows visible side faces when capturing the top face.
 * The top face and opposite (bottom) face are NOT shown in the ring.
 */
export const DICE_GEOMETRY: Record<DiceType, DiceFaceGeometry> = {
  /**
   * D4 (Tetrahedron) - 3 ring positions at 120 degrees
   * Special: No true opposite faces. All 3 other faces visible when 1 is on top.
   * Read from vertex-up position (upward point shows value).
   */
  d4: {
    visibleSideCount: 3,
    ringAngle: 120,
    oppositeSum: null,
    // Right-handed standard: 1-2-3 and 1-3-4 clockwise at vertex
    // Camera views from opposite side, so those appear reversed
    sideMap: {
      1: [2, 3, 4],  // 2-3-4 CCW at vertex → CW from camera
      2: [1, 4, 3],  // 1-3-4 CW at vertex → CCW from camera
      3: [1, 2, 4],  // 1-2-4 CCW at vertex → CW from camera
      4: [1, 3, 2],  // 1-2-3 CW at vertex → CCW from camera
    },
    oppositeMap: {},
  },

  /**
   * D6 (Cube) - 4 ring positions at 90 degrees
   * Opposite faces sum to 7: 1-6, 2-5, 3-4
   * When one face is up, 4 side faces visible, opposite hidden.
   */
  d6: {
    visibleSideCount: 4,
    ringAngle: 90,
    oppositeSum: 7,
    sideMap: {
      1: [2, 3, 5, 4],
      2: [1, 4, 6, 3],
      3: [1, 2, 6, 5],
      4: [1, 5, 6, 2],
      5: [1, 3, 6, 4],
      6: [2, 4, 5, 3],
    },
    oppositeMap: { 1: 6, 2: 5, 3: 4, 4: 3, 5: 2, 6: 1 },
  },

  /**
   * D8 (Octahedron) - 3 ring positions at 120 degrees
   * Opposite faces sum to 9: 1-8, 2-7, 3-6, 4-5
   * Triangular face up means 3 adjacent triangles visible.
   * Each face shares edges with exactly 3 other faces.
   */
  d8: {
    visibleSideCount: 3,
    ringAngle: 120,
    oppositeSum: 9,
    sideMap: {
      1: [2, 3, 4],   // Adjacent to 2, 3, 4 (opposite is 8)
      2: [1, 5, 6],   // Adjacent to 1, 5, 6 (opposite is 7)
      3: [1, 4, 7],   // Adjacent to 1, 4, 7 (opposite is 6)
      4: [1, 3, 8],   // Adjacent to 1, 3, 8 (opposite is 5)
      5: [2, 6, 8],   // Adjacent to 2, 6, 8 (opposite is 4)
      6: [2, 5, 7],   // Adjacent to 2, 5, 7 (opposite is 3)
      7: [3, 6, 8],   // Adjacent to 3, 6, 8 (opposite is 2)
      8: [4, 5, 7],   // Adjacent to 4, 5, 7 (opposite is 1)
    },
    oppositeMap: { 1: 8, 2: 7, 3: 6, 4: 5, 5: 4, 6: 3, 7: 2, 8: 1 },
  },

  /**
   * D10 (Pentagonal Trapezohedron) - 5 ring positions at 72 degrees
   * Internal: Uses 1-10 numbering (system uses 1-based faces).
   * Display: Face 10 displays as "0" on physical dice.
   * Opposite faces (display): 0+9=9, 1+8=9, 2+7=9, 3+6=9, 4+5=9
   * Internal opposites: 10↔9, 1↔8, 2↔7, 3↔6, 4↔5
   * On a D10, each face is adjacent to 5 non-opposite faces.
   */
  d10: {
    visibleSideCount: 5,
    ringAngle: 72,
    oppositeSum: 9, // Display values 0-9, opposites sum to 9
    sideMap: {
      // Internal 1-10 numbering. Adjacent = all faces except self and opposite.
      1: [2, 3, 4, 5, 10],  // All except 1 (self) and 8 (opposite)
      2: [1, 3, 4, 5, 10],  // All except 2 (self) and 7 (opposite)
      3: [1, 2, 4, 5, 10],  // All except 3 (self) and 6 (opposite)
      4: [1, 2, 3, 6, 10],  // All except 4 (self) and 5 (opposite)
      5: [1, 2, 3, 6, 10],  // All except 5 (self) and 4 (opposite)
      6: [1, 2, 7, 8, 9],   // All except 6 (self) and 3 (opposite)
      7: [1, 6, 8, 9, 10],  // All except 7 (self) and 2 (opposite)
      8: [6, 7, 9, 10, 4],  // All except 8 (self) and 1 (opposite)
      9: [3, 4, 5, 6, 7],   // All except 9 (self) and 10 (opposite)
      10: [4, 5, 6, 7, 8],  // All except 10 (self) and 9 (opposite)
    },
    oppositeMap: { 1: 8, 2: 7, 3: 6, 4: 5, 5: 4, 6: 3, 7: 2, 8: 1, 9: 10, 10: 9 },
  },

  /**
   * D12 (Dodecahedron) - 5 ring positions at 72 degrees
   * Opposite faces sum to 13: 1-12, 2-11, 3-10, 4-9, 5-8, 6-7
   * Each pentagonal face shares edges with exactly 5 adjacent faces.
   */
  d12: {
    visibleSideCount: 5,
    ringAngle: 72,
    oppositeSum: 13,
    sideMap: {
      // Each face is adjacent to exactly 5 faces (not opposite, not self)
      // Based on standard dodecahedron geometry
      1: [2, 3, 4, 5, 6],     // Opposite is 12
      2: [1, 3, 6, 8, 10],    // Opposite is 11
      3: [1, 2, 4, 8, 9],     // Opposite is 10
      4: [1, 3, 5, 7, 11],    // Opposite is 9
      5: [1, 4, 6, 10, 11],   // Opposite is 8
      6: [1, 2, 5, 9, 10],    // Opposite is 7
      7: [4, 8, 9, 11, 12],   // Opposite is 6
      8: [2, 3, 7, 10, 12],   // Opposite is 5
      9: [3, 6, 7, 11, 12],   // Opposite is 4
      10: [2, 5, 6, 8, 12],   // Opposite is 3
      11: [4, 5, 7, 9, 12],   // Opposite is 2
      12: [7, 8, 9, 10, 11],  // Opposite is 1
    },
    oppositeMap: { 1: 12, 2: 11, 3: 10, 4: 9, 5: 8, 6: 7, 7: 6, 8: 5, 9: 4, 10: 3, 11: 2, 12: 1 },
  },

  /**
   * D20 (Icosahedron) - 5 ring positions at 72 degrees
   * Opposite faces sum to 21: 1-20, 2-19, etc.
   * Triangular face up means 5 adjacent triangles visible (surrounding top).
   */
  d20: {
    visibleSideCount: 5,
    ringAngle: 72,
    oppositeSum: 21,
    sideMap: {
      // Each triangular face is adjacent to exactly 3 faces (sharing edges)
      // We show 5 nearby visible faces. Opposite of N is (21-N).
      // Must exclude self and opposite from each array.
      1: [2, 3, 4, 5, 8],       // Opposite is 20
      2: [1, 3, 6, 9, 10],      // Opposite is 19
      3: [1, 2, 4, 10, 11],     // Opposite is 18
      4: [1, 3, 5, 11, 12],     // Opposite is 17
      5: [1, 4, 8, 12, 13],     // Opposite is 16
      6: [2, 7, 9, 14, 18],     // Opposite is 15
      7: [6, 8, 16, 17, 18],    // Opposite is 14
      8: [1, 5, 7, 17, 20],     // Opposite is 13
      9: [2, 6, 10, 14, 18],    // Opposite is 12
      10: [2, 3, 9, 18, 19],    // Opposite is 11
      11: [3, 4, 12, 19, 20],   // Opposite is 10
      12: [4, 5, 11, 13, 20],   // Opposite is 9
      13: [5, 12, 17, 19, 20],  // Opposite is 8
      14: [6, 9, 15, 16, 18],   // Opposite is 7
      15: [7, 14, 16, 17, 19],  // Opposite is 6
      16: [7, 8, 14, 15, 17],   // Opposite is 5 - fixed (removed 5)
      17: [7, 8, 15, 16, 20],   // Opposite is 4
      18: [6, 7, 9, 10, 14],    // Opposite is 3 - fixed (removed 3)
      19: [10, 11, 13, 15, 20], // Opposite is 2 - fixed (removed 2)
      20: [8, 11, 12, 13, 17],  // Opposite is 1 - fixed (removed 1)
    },
    oppositeMap: {
      1: 20, 2: 19, 3: 18, 4: 17, 5: 16, 6: 15, 7: 14, 8: 13, 9: 12, 10: 11,
      11: 10, 12: 9, 13: 8, 14: 7, 15: 6, 16: 5, 17: 4, 18: 3, 19: 2, 20: 1,
    },
  },
};

/** Registration capture mode - manual face-by-face or video rolling */
export type CaptureMode = 'manual' | 'video';

/**
 * Tracks image coverage for a single dice face during registration.
 */
export interface FaceCoverage {
  /** Face number (1-N based on dice type) */
  faceNumber: number;
  /** Number of images captured for this face */
  imageCount: number;
  /** Target number of images needed */
  imagesNeeded: number;
  /** Whether this face has sufficient coverage */
  isComplete: boolean;
}

/**
 * Captures metadata for a single dice roll during video registration.
 */
export interface RollCapture {
  /** Sequential roll number in this session */
  rollNumber: number;
  /** ISO8601 timestamp of capture */
  timestamp: string;
  /** Auto-detected face value, null if detection failed */
  detectedFace: number | null;
  /** Confidence score of face detection (0-1) */
  confidence: number;
  /** Number of usable frames extracted from this roll */
  imageCount: number;
  /** S3 keys of uploaded images from this roll */
  s3Keys: string[];
}

/**
 * D4 requires 3 rotations per result value for comprehensive training.
 * A d4 tetrahedron has 4 landing positions × 3 rotations = 12 distinct orientations.
 */
export const D4_ROTATIONS_PER_RESULT = 3;

/**
 * Training image counts for dice with special requirements.
 * D4 needs 12 images (4 results × 3 rotations) instead of just 4.
 */
export const TRAINING_IMAGE_COUNTS: Partial<Record<DiceType, number>> = {
  d4: 12, // 4 results × 3 rotations
};

/**
 * Get the number of training images required for a dice type.
 * Returns TRAINING_IMAGE_COUNTS value if defined, otherwise FACE_COUNTS.
 * @param diceType - The type of dice
 * @returns Number of images to capture for training
 */
export function getTrainingImageCount(diceType: DiceType): number {
  return TRAINING_IMAGE_COUNTS[diceType] ?? FACE_COUNTS[diceType];
}

/**
 * Get the result value (class label) for a d4 face number.
 * Maps capture index (1-12) to dice result (1-4).
 * @param faceNumber - Sequential capture index (1-12)
 * @returns Dice result value (1-4)
 */
export function getD4ResultValue(faceNumber: number): number {
  return Math.ceil(faceNumber / D4_ROTATIONS_PER_RESULT);
}

/**
 * Get the rotation number within a result for d4.
 * @param faceNumber - Sequential capture index (1-12)
 * @returns Rotation number (1-3)
 */
export function getD4RotationNumber(faceNumber: number): number {
  return ((faceNumber - 1) % D4_ROTATIONS_PER_RESULT) + 1;
}

export type RegistrationStatus = 'uploading' | 'training' | 'ready' | 'failed';
export type ModelStatus = 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING';

// ========== Provider Config Types (TICKET-166) ==========

/**
 * Recognition providers available in the pipeline.
 * - 'rekognition': AWS Rekognition text detection (standard)
 * - 'rekognition-text': Alias for standard Rekognition text detection
 * - 'pip-counter': AWS Rekognition label-based pip counting (d6 only)
 * - 'openai': OpenAI GPT-4 Vision (default LLM fallback)
 * - 'claude': Anthropic Claude Opus 4.7 (premium LLM fallback, login-gated)
 * - 'consensus': Virtual provider representing multi-provider consensus output
 * - 'onnx': Custom ONNX model trained via dice-trainer (runs in dedicated Lambda)
 * - 'rekognition-custom': AWS Rekognition Custom Labels (trained per dice type)
 */
export type RecognitionProvider =
  | 'rekognition'
  | 'rekognition-text'
  | 'pip-counter'
  | 'openai'
  | 'claude'
  | 'consensus'
  | 'onnx'
  | 'rekognition-custom';

/**
 * Per-device, per-dice-type recognition provider configuration.
 * Stored in DynamoDB as PK: DEVICE#{deviceId}, SK: PROVIDER_CONFIG#{diceType}.
 */
export interface ProviderConfig {
  /** The device this config applies to */
  deviceId: string;
  /** The dice type this config applies to */
  diceType: DiceType;
  /** Ordered list of active providers — tried left-to-right; exactly one LLM ('openai' XOR 'claude') always last */
  activeProviders: RecognitionProvider[];
  /** ISO 8601 timestamp of last update */
  updatedAt: string;
  /** Cognito user ID that owns this config (absent for legacy/guest data) */
  userId?: string;
}

/**
 * GET response for provider config endpoint.
 * Returns which providers are active and which are available.
 */
export interface ProviderConfigResponse {
  /** Currently active providers for this device+diceType */
  activeProviders: RecognitionProvider[];
  /** All providers with training/availability status */
  availableProviders: AvailableProvider[];
}

/**
 * An individual provider entry with training status for UI display.
 */
export interface AvailableProvider {
  /** Provider identifier */
  id: RecognitionProvider;
  /** Human-readable label for UI display */
  label: string;
  /** Whether this provider is trained and ready to use */
  trained: boolean;
  /** Training status (present when trained is false and training is in progress) */
  trainingStatus?: string;
  /**
   * True if using this provider requires an authenticated user.
   * UI renders an auth gate (disabled row + sign-up CTA) for guests when set.
   */
  requiresAuth?: boolean;
}

/**
 * Dominant tray color detected from registration images (TICKET-263).
 * Used at inference time to isolate the die from the tray background.
 */
export interface TrayColorRgb {
  /** Red channel (0-255) */
  r: number;
  /** Green channel (0-255) */
  g: number;
  /** Blue channel (0-255) */
  b: number;
  /** Mean Euclidean distance of corner pixels from the mean color. Higher = less reliable. */
  variance: number;
}

/**
 * Dice registration record stored in DynamoDB
 * PK: DEVICE#{deviceId}, SK: DICE_REG#{diceType}
 */
export interface DiceRegistration {
  registrationId: string;
  deviceId: string;
  diceType: DiceType;
  /** User-friendly name, e.g., "Blue Sparkle d20" */
  diceName?: string;
  status: RegistrationStatus;
  /** Rekognition model ARN when ready */
  modelArn?: string;
  modelStatus?: ModelStatus;
  /** S3 path prefix for images */
  s3Prefix: string;
  facesUploaded: number;
  totalFaces: number;
  createdAt: string;
  updatedAt: string;
  trainedAt?: string;
  /** Last inference timestamp for idle model tracking */
  lastInferenceAt?: string;
  /** Total inference count for usage tracking */
  inferenceCount?: number;
  /** Custom rotations per face preference set by user during registration */
  customRotationsPerFace?: number;
  /** Cognito user ID that owns this registration (absent for legacy/guest data) */
  userId?: string;
  /** Dominant tray color detected from registration images (TICKET-263) */
  trayColorRgb?: TrayColorRgb;
}

/**
 * Face upload record stored in DynamoDB
 * PK: DICE_REG#{registrationId}, SK: FACE#{faceNumber, zero-padded to 3 digits}
 */
export interface FaceUpload {
  registrationId: string;
  faceNumber: number;
  s3Key: string;
  uploadedAt: string;
  validated: boolean;
}

// ========== API Request/Response Types ==========

export interface StartRegistrationRequest {
  deviceId: string;
  diceType: DiceType;
  diceName?: string;
}

export interface UploadUrl {
  face: number;
  /** Presigned S3 PUT URL */
  url: string;
}

export interface StartRegistrationResponse {
  registrationId: string;
  uploadUrls: UploadUrl[];
  /** When presigned URLs expire */
  expiresAt: string;
}

export interface UploadFaceRequest {
  registrationId: string;
  faceNumber: number;
}

export interface CompleteRegistrationRequest {
  registrationId: string;
}

export interface CompleteRegistrationResponse {
  status: 'training' | 'ready' | 'failed';
  message: string;
  /** e.g., "2-4 hours" */
  estimatedTrainingTime?: string;
}

export interface GetRegistrationsResponse {
  registrations: DiceRegistration[];
}

export interface GetRegistrationResponse {
  registration: DiceRegistration;
  faces: FaceUpload[];
}

export interface DeleteRegistrationResponse {
  success: boolean;
  message: string;
}

export interface ModelControlResponse {
  status: ModelStatus;
  message: string;
}

// ========== WebSocket Message Types ==========

export interface RegistrationProgressMessage {
  type: 'registrationProgress';
  registrationId: string;
  facesUploaded: number;
  totalFaces: number;
  currentFace: number;
}

export interface TrainingStatusMessage {
  type: 'trainingStatus';
  registrationId: string;
  status: 'started' | 'progress' | 'complete' | 'failed';
  /** 0-100 percentage */
  progress?: number;
  modelArn?: string;
  error?: string;
}

export interface ModelStatusMessage {
  type: 'modelStatus';
  registrationId: string;
  modelStatus: ModelStatus;
}

// ========== Error Types ==========

export interface DiceRegistryError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export const ERROR_CODES = {
  REGISTRATION_NOT_FOUND: 'REGISTRATION_NOT_FOUND',
  INVALID_DICE_TYPE: 'INVALID_DICE_TYPE',
  INVALID_FACE_NUMBER: 'INVALID_FACE_NUMBER',
  UPLOAD_INCOMPLETE: 'UPLOAD_INCOMPLETE',
  TRAINING_FAILED: 'TRAINING_FAILED',
  MODEL_NOT_READY: 'MODEL_NOT_READY',
  MODEL_START_TIMEOUT: 'MODEL_START_TIMEOUT',
} as const;

// ========== Image Upload URL Types (TICKET-087) ==========

/**
 * Request body for generating a presigned URL for a specific image
 */
export interface ImageUploadUrlRequest {
  /** 1-based index of the image for this face */
  imageIndex: number;
}

/**
 * Response containing presigned URL and unique S3 key for image upload
 */
export interface ImageUploadUrlResponse {
  /** Presigned PUT URL for uploading the image */
  uploadUrl: string;
  /** Unique S3 key for this specific image */
  s3Key: string;
  /** When the presigned URL expires */
  expiresAt: string;
}

// ========== List Images Types (TICKET-088) ==========

/**
 * Represents a single image stored in S3 for a dice registration
 */
export interface RegistrationImage {
  /** Face number this image belongs to (1-N based on dice type) */
  faceNumber: number;
  /** Image index within the face (1-based) */
  imageIndex: number;
  /** Full S3 key for this image */
  s3Key: string;
  /** Presigned GET URL for viewing/downloading the image */
  downloadUrl: string;
}

/**
 * Response from listing all images for a registration
 */
export interface ListImagesResponse {
  /** All images for this registration */
  images: RegistrationImage[];
  /** Total number of images */
  totalImages: number;
}
